import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { CommandStatus } from '../api/api-reference';
import { SignalRService } from './signalr.service';

export interface RelayClientResponse {
  commandId: string;
  meterId: string;
  commandStatus: CommandStatus;
  /** The relay state the meter ended up in. Only sent when the command succeeded. */
  status?: boolean;
  errorMessage?: string;
}

/**
 * Owns the single 'disconnect-control' hub handler and fans results out over rxjs.
 * SignalRService keeps only one handler per event name, so components must not
 * register 'disconnect-control' themselves - they subscribe to results$ instead.
 */
@Injectable({ providedIn: 'root' })
export class RelayStateService {
  private readonly notificationPath = '/notification';
  private readonly eventName = 'disconnect-control';

  private readonly results = new Subject<RelayClientResponse>();
  readonly results$: Observable<RelayClientResponse> = this.results.asObservable();

  constructor(private signalRService: SignalRService) {}

  // Safe to call from every consumer: re-registering our own callback replaces it with itself.
  async ensureSubscribed(): Promise<void> {
    try {
      await this.signalRService.startNewConnection(this.notificationPath);
      this.signalRService.registerHandler(
        this.notificationPath,
        this.eventName,
        (message: RelayClientResponse) => this.results.next(message),
      );
    } catch (error) {
      console.error('[RelayState] Failed to subscribe to relay events:', error);
    }
  }
}
